/**
 * A property's brand colour, parsed and judged once for every surface.
 *
 * The host app, the guest portal and the landing page all pick a readable
 * text/ink colour to sit on top of `properties.color`. Each surface keeps its
 * own ink tokens, but the parse and the light-or-dark decision live here so
 * the same brand colour can never read as "light" in one app and "dark" in
 * another.
 */

const HEX_COLOR_RE = /^#?(?<hex>[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/u;

type Rgb = [number, number, number];

/**
 * Parse a `#rgb` / `#rrggbb` colour (leading `#` optional — stored values
 * predating validation may lack it) into `[r, g, b]` channels in 0-255.
 * Strict otherwise: `null` for partial hex, stray characters, or any other
 * format — `parseInt` alone would happily eat `1Z` as `1`.
 */
function parseHexColor(value: string | null | undefined): Rgb | null {
  if (!value) {
    return null;
  }
  const groups = value.trim().match(HEX_COLOR_RE)?.groups;
  if (!groups) {
    return null;
  }
  const { hex } = groups;
  const pair = (i: number) =>
    hex.length === 3
      ? Number.parseInt(hex[i] + hex[i], 16)
      : Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return [pair(0), pair(1), pair(2)];
}

/**
 * The colour as canonical lowercase `#rrggbb`, or null when it doesn't parse.
 * Write THIS into CSS custom properties — a stored value without its leading
 * `#` would otherwise emit invalid CSS while the foreground still applied.
 */
export function normalizeHexColor(
  value: string | null | undefined
): string | null {
  const rgb = parseHexColor(value);
  if (!rgb) {
    return null;
  }
  return toHex(rgb);
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2.x relative luminance of an `[r, g, b]` triple in 0-255. */
function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Whether dark ink is readable on this colour, or null when it doesn't parse.
 * WCAG 2.x relative luminance; 0.179 is the threshold where black-on-colour
 * clears the 4.5:1 contrast ratio for most palette colours — below it, white
 * ink wins.
 */
export function isLightColor(value: string | null | undefined): boolean | null {
  const rgb = parseHexColor(value);
  if (!rgb) {
    return null;
  }
  return relativeLuminance(rgb) > 0.179;
}

/** WCAG 2.x contrast ratio between two relative luminances. */
function contrastRatio(a: number, b: number): number {
  const [lighter, darker] = a >= b ? [a, b] : [b, a];
  return (lighter + 0.05) / (darker + 0.05);
}

function toHsl([r, g, b]: Rgb): [number, number, number] {
  const [red, green, blue] = [r / 255, g / 255, b / 255];
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) {
    return [0, 0, lightness];
  }
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue: number;
  if (max === red) {
    hue = ((green - blue) / delta) % 6;
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }
  return [(((hue * 60) % 360) + 360) % 360, saturation, lightness];
}

function fromHsl(hue: number, saturation: number, lightness: number): Rgb {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = Math.min(Math.floor(hue / 60), 5);
  const second = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  // Which of the three channels carries the full chroma, which carries the
  // ramp, and which is zero, per 60° sector of the hue wheel.
  const bySector: Rgb[] = [
    [chroma, second, 0],
    [second, chroma, 0],
    [0, chroma, second],
    [0, second, chroma],
    [second, 0, chroma],
    [chroma, 0, second],
  ];
  const offset = lightness - chroma / 2;
  return bySector[sector].map((c) => Math.round((c + offset) * 255)) as Rgb;
}

/** Alpha-composite `source` over `backdrop`, the way the browser draws a
 *  `bg-brand/10` wash on the page background. */
function composite(source: Rgb, backdrop: Rgb, alpha: number): Rgb {
  return source.map((c, i) =>
    Math.round(c * alpha + backdrop[i] * (1 - alpha))
  ) as Rgb;
}

/** WCAG AA's 4.5:1 for normal text, plus headroom for the rounding by which
 *  the browser's blend of the wash and axe's measure of it differ from
 *  `composite`: aiming at exactly 4.5 lands some colours at 4.49. */
const TEXT_CONTRAST_TARGET = 4.6;
/** The guest portal's page background per theme: `--background` and `--card`,
 *  which that app leaves equal in both. Not every surface in the monorepo: the
 *  host app raises its dark `--card` to `oklch(0.205 0 0)`, so dark ink chosen
 *  here clears AA there only on the page background, not on a card. */
const PAGE_SURFACE = {
  dark: [10, 10, 10],
  light: [255, 255, 255],
} satisfies Record<string, Rgb>;
/** Brand text sits on a `bg-brand/10` wash as often as on the bare page, so
 *  the ink has to clear both. */
const BRAND_TINT_ALPHA = 0.1;
/** One step of HSL lightness per attempt: fine enough that the result is the
 *  least shift that clears AA, coarse enough to settle in a few dozen. */
const LIGHTNESS_STEP = 0.01;

type BrandTheme = keyof typeof PAGE_SURFACE;

/**
 * The brand colour for TEXT on the guest portal's page in `theme` (see
 * `PAGE_SURFACE`), darkened on the light page or lightened on the dark one by
 * the least HSL lightness that clears AA on the page and the `bg-brand/10`
 * wash; hue and saturation are kept, and a legible colour comes back unchanged.
 * Null when the colour doesn't parse.
 *
 * HSL saturation inflates as lightness nears 0, so a near-black brand lightens
 * into a vivid mid tone on the dark page: nothing legible there reads as
 * near-black.
 */
export function brandText(
  value: string | null | undefined,
  theme: BrandTheme
): string | null {
  const rgb = parseHexColor(value);
  if (!rgb) {
    return null;
  }
  const page = PAGE_SURFACE[theme];
  const surfaces = [
    relativeLuminance(page),
    relativeLuminance(composite(rgb, page, BRAND_TINT_ALPHA)),
  ];
  // Light ink loses contrast as the backdrop lightens, dark ink as it darkens,
  // so each has to clear the harder of the two surfaces.
  const lighten = theme === "dark";
  const backdrop = lighten ? Math.max(...surfaces) : Math.min(...surfaces);
  const step = lighten ? LIGHTNESS_STEP : -LIGHTNESS_STEP;
  const [hue, saturation, lightness] = toHsl(rgb);
  for (let l = lightness; l >= 0 && l <= 1; l += step) {
    const candidate = fromHsl(hue, saturation, l);
    if (
      contrastRatio(relativeLuminance(candidate), backdrop) >=
      TEXT_CONTRAST_TARGET
    ) {
      return toHex(candidate);
    }
  }
  return lighten ? "#ffffff" : "#000000";
}
